import cx from "classnames";
import { motion } from "framer-motion";
import React, { useMemo } from "react";

import { IOrderBookItem } from "lib/apis/markets";
import { formatNumber } from "lib/numbers";

interface Props {
  maxTotal: number;
  orders: IOrderBookItem[];
  side: "bids" | "asks";
  targetRowCount: number;
}

function getRowSeed(key: string) {
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
  }
  return hash;
}

function getRowMotionProfile(key: string, index: number, percentage: number, side: "bids" | "asks") {
  const seed = getRowSeed(`${key}:${side}:${index}`);
  const base = (seed % 1000) / 1000;
  const secondary = ((seed >> 3) % 1000) / 1000;
  const widthPulse = 0.008 + base * 0.018;

  return {
    pulseDuration: 2 + secondary * 2.2,
    pulseDelay: -(0.15 + base * 1.8),
    widthPulse,
    fillOpacity: `${0.3 + secondary * 0.18}`,
    spring: {
      type: "spring" as const,
      stiffness: 180 + Math.round(base * 120),
      damping: 22 + Math.round(secondary * 8),
      mass: 0.52 + base * 0.28,
    },
    width: `${percentage}%`,
  };
}

const OrderBookTable = ({ maxTotal, orders, side, targetRowCount }: Props) => {
  const paddedOrders = useMemo(() => {
    if (orders.length >= targetRowCount) {
      return orders.slice(0, targetRowCount);
    }

    return [...orders, ...Array.from({ length: targetRowCount - orders.length }, () => null)];
  }, [orders, targetRowCount]);

  return (
    <div className="OrderBook-table">
      {paddedOrders.map((order, index) => {
        if (!order) {
          return (
            <div key={`${side}:placeholder:${index}`} className="OrderBook-row OrderBook-row--placeholder">
              <span className="OrderBook-cell OrderBook-cell--placeholder">&nbsp;</span>
              <span className="OrderBook-cell OrderBook-cell--placeholder">&nbsp;</span>
              <span className="OrderBook-cell OrderBook-cell--placeholder">&nbsp;</span>
            </div>
          );
        }

        const percentage = maxTotal > 0 ? (parseFloat(order.totalInToken) / maxTotal) * 100 : 0;
        const rowKey = `${side}:${order.price}`;
        const motionProfile = getRowMotionProfile(rowKey, index, percentage, side);
        return (
          <motion.div
            key={rowKey}
            layout="position"
            // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
            transition={{ type: "spring", stiffness: 320, damping: 30, mass: 0.55 }}
            className="OrderBook-row"
          >
            <motion.div
              className={cx("OrderBook-row-fill", {
                "OrderBook-row-fill--bids": side === "bids",
                "OrderBook-row-fill--asks": side === "asks",
              })}
              // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
              style={{
                ["--fill-opacity" as string]: motionProfile.fillOpacity,
              }}
              initial={false}
              // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
              animate={{
                width: motionProfile.width,
                scaleX: [1, 1 + motionProfile.widthPulse, 1 - motionProfile.widthPulse * 0.4, 1],
              }}
              // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
              transition={{
                width: motionProfile.spring,
                scaleX: {
                  duration: motionProfile.pulseDuration,
                  delay: motionProfile.pulseDelay,
                  ease: "easeInOut",
                  repeat: Number.POSITIVE_INFINITY,
                  repeatType: "mirror",
                },
              }}
            />
            <span
              className={cx("OrderBook-cell OrderBook-cell--price", {
                "OrderBook-cell--bid": side === "bids",
                "OrderBook-cell--ask": side === "asks",
              })}
            >
              {formatNumber(order.price, { decimals: 6, isForceDecimal: false })}
            </span>
            <span className="OrderBook-cell">
              {formatNumber(order.sizeInToken, { decimals: 4, isForceDecimal: false })}
            </span>
            <span className="OrderBook-cell overflow-hidden text-right">
              {formatNumber(order.totalInToken, { decimals: 4, isForceDecimal: false })}
            </span>
          </motion.div>
        );
      })}
    </div>
  );
};

export default OrderBookTable;
